import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { Star } from "lucide-react";
import { motion } from "framer-motion";

import "swiper/css";
import "swiper/css/pagination";

const ReviewCarousel = () => {
  const { t } = useTranslation();

  const reviews = [
    { id: "nadia", stars: 5 },
    { id: "virginie", stars: 5 },
    { id: "catherine", stars: 5 },
    { id: "manjit", stars: 5 },
    { id: "diego", stars: 5 },
  ];

  return (
    <section className="relative py-28 sm:py-36 lg:py-44 px-6 sm:px-8 lg:px-12 overflow-hidden bg-[#F8F5EF]">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-5 mb-16 sm:mb-20 max-w-xl">
          <span className="font-oswald text-[11px] sm:text-xs uppercase tracking-[0.34em] text-[#86523d] whitespace-nowrap">
            {t("home.reviews.eyebrow", "Trusted by clients")}
          </span>
          <span className="h-px flex-1 bg-stone-300/80" aria-hidden="true" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20 xl:gap-28 items-start">
          <motion.header
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <h2 className="font-oswald font-medium uppercase tracking-[-0.035em] text-[clamp(2.7rem,5vw,4.8rem)] text-stone-900 leading-[0.98]">
              {t("home.reviews.title", "What our clients say")}
            </h2>

            <p className="mt-8 max-w-md font-playfair text-lg sm:text-xl leading-relaxed text-stone-600">
              {t(
                "home.reviews.subtitle",
                "Every story reflects our commitment to excellence, discretion, and results."
              )}
            </p>

            <a
              href="https://share.google/8sgaQ4Pmj4ThZrxvx"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 mt-10 font-oswald text-[11px] uppercase tracking-[0.26em] text-[#86523d]"
            >
              {t("home.reviews.google_link_label", "Read all reviews on")} Google
              <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">→</span>
            </a>
          </motion.header>

          <div className="lg:col-span-7 min-w-0">
            <Swiper
              aria-label="Client reviews carousel"
              modules={[Autoplay, Pagination]}
              slidesPerView={1}
              spaceBetween={40}
              autoplay={{ delay: 6500, disableOnInteraction: false }}
              pagination={{ clickable: true }}
              loop
              className="reviews-swiper !pb-20 [--swiper-pagination-color:#a26850] [--swiper-pagination-bullet-inactive-color:#a8a29e] [--swiper-pagination-bullet-inactive-opacity:0.35]"
            >
              {reviews.map((review, idx) => (
                <SwiperSlide key={review.id}>
                  <motion.article
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    viewport={{ once: true }}
                    className="relative"
                  >
                    <div className="flex items-start justify-between gap-6 mb-0">
                      <span className="font-playfair text-7xl sm:text-8xl leading-[0.35] text-[#86523d]" aria-hidden="true">
                        “
                      </span>
                      <span className="font-oswald text-[10px] tracking-[0.24em] text-stone-600">
                        {String(idx + 1).padStart(2, "0")} / {String(reviews.length).padStart(2, "0")}
                      </span>
                    </div>

                    <blockquote className="-mt-4">
                      <p className="font-playfair text-2xl sm:text-3xl lg:text-[2rem] leading-[1.45] text-stone-800">
                        {t(`home.reviews.items.${review.id}.comment`)}
                      </p>
                    </blockquote>

                    <div className="mt-3 flex flex-wrap items-end justify-between gap-6">
                      <div>
                        <h3 className="font-oswald text-sm uppercase tracking-[0.24em] text-stone-900">
                          {t(`home.reviews.items.${review.id}.name`)}
                        </h3>
                        <span className="block mt-2 font-playfair italic text-sm text-stone-500">
                          {t("home.reviews.verified", "Verified client")} · Google
                        </span>
                      </div>

                      <div role="img" className="flex gap-1" aria-label={`${review.stars} stars`}>
                        {[...Array(review.stars)].map((_, i) => (
                          <Star
                            key={i}
                            size={14}
                            fill="#a26850"
                            color="#a26850"
                            aria-hidden="true"
                          />
                        ))}
                      </div>
                    </div>
                  </motion.article>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewCarousel;
